import React, { useCallback, useEffect, useState } from 'react';
import { CenterFrameLayout } from 'Layouts';
import { addBangoSubscription } from 'Api/AccountApi';
import { getEntitlements } from 'State/User';
import { encodeSearchParams } from 'Utils/Path';
import { localStorageGet, localStorageSet } from 'Utils/Storage';
import { StaticLinkButton } from 'Components/Fields/Buttons';
import { useStore } from 'Hooks/useStore';
import { Confirmation } from 'Pages/Confirmation/Confirmation';
import './BangoFinal.css';

export const BangoFinal = () => {
  const {
    routes,
    defaultConfig: { rootDomain },
  } = useStore('appConfig');
  const { subscriptionId, redirectUrl, isSiriusXM, productId } = useStore('integrations.bango');
  const [view, setView] = useState('LOADING'); // 'LOADING' 'SUCCESS' 'INELIGIBLE' 'ERROR'
  const [addBangoSuccess, setAddBangoSuccess] = useState(false);

  const { isStatusSuccess, hasActiveSubscription, isPendingCancel, purchaseType } = useStore('subscription');

  //Including rootDomain so reloads site and pulls params again after sign out
  //URL comes in encoded from Bango, so encoding it again.
  const returnToSearchParams = encodeSearchParams({
    subscriptionRequestId: subscriptionId,
    redirect_uri: redirectUrl,
    productId,
  });
  const returnToUrl = `${rootDomain}${routes.signupCreateAccount}?${returnToSearchParams}`;
  const searchParams = encodeSearchParams({ return_to: returnToUrl });
  const createAccountUrl = `${routes.signout}?${searchParams}`;

  const text = {
    LOADING: {
      SHOW_LOADER: true,
      TITLE: 'Checking your subscription',
    },
    SUCCESS: {
      TITLE: 'Congratulations!',
      BODY: `Your account has been successfully activated with ${
        isSiriusXM ? 'SiriusXM' : 'Verizon +play'
      } subscription. Enjoy Fox Nation!`,
      PRIMARY_BUTTON: `Continue to ${isSiriusXM ? 'SiriusXM' : 'Verizon'}`,
      PRIMARY_PATH: redirectUrl,
    },
    INELIGIBLE: {
      TITLE: 'Good News!',
      BODY: `It looks like you are already a valued Fox Nation subscriber. If you would like to purchase an additional subscription through ${
        isSiriusXM ? 'SiriusXM' : 'Verizon +play'
      }, please use a new email address.`,
      PRIMARY_BUTTON: 'Yes, take me back to Create Account',
      PRIMARY_PATH: createAccountUrl,
      SECONDARY_BUTTON: `No, take me back to ${isSiriusXM ? 'SiriusXM' : 'Verizon +play'}`,
      SECONDARY_PATH: redirectUrl,
    },
    ERROR: {
      TITLE: 'Something went wrong',
      BODY: 'Please re-start the activation process',
      PRIMARY_BUTTON: `Back to ${isSiriusXM ? 'SiriusXM' : 'Verizon'}`,
      PRIMARY_PATH: redirectUrl,
    },
  };

  const addBango = useCallback(async () => {
    const { isSuccess } = await addBangoSubscription(subscriptionId);
    if (isSuccess) {
      localStorageSet('successfulSubscriptionId', subscriptionId);
      setAddBangoSuccess(true);
      // Getting entitlements shouldn't be necessary since user will be going to Bango after sign up
      // Doing it just to be extra safe
      getEntitlements();
    }
    setView('ERROR');
  }, [subscriptionId]);

  useEffect(() => {
    if (isStatusSuccess) {
      const successfulSubscriptionId = localStorageGet('successfulSubscriptionId');

      const hasBangoSub =
        addBangoSuccess ||
        ((hasActiveSubscription || isPendingCancel) &&
          (purchaseType === 'verizonBango' || purchaseType === 'siriusXMBango') &&
          !!successfulSubscriptionId &&
          successfulSubscriptionId === subscriptionId);

      /* only show SUCCESS if:
        just added bango
        or
          1) have active sub
          2) that was purchased via bango and
          3) not trying to make a new bango purchase (not a new subscriptionId)

        This accounts for various edge cases - e.g. 
          if user refreshes this page after success,
          or refreshes after cancelling their subscription on Bango,
          or refreshes after cancelling and then buying a new non-Bango sub in a different browser
          or if user tries to add a Bango sub after just adding one
      */
      if (hasBangoSub) {
        setView('SUCCESS');
      } else if (hasActiveSubscription || isPendingCancel) {
        setView('INELIGIBLE');
      } else if (subscriptionId && !hasBangoSub) {
        addBango();
      } else {
        setView('ERROR');
      }
    }
  }, [
    isStatusSuccess,
    hasActiveSubscription,
    subscriptionId,
    purchaseType,
    addBangoSuccess,
    isPendingCancel,
    addBango,
  ]);

  // Only SiriusXM gets a normal confirmation page
  // For Verizon, we agreed with them to CTA users back to Verizon
  if (view === 'SUCCESS' && isSiriusXM) {
    return <Confirmation customSubtitle={text[view].BODY} customMobileSubtitle={text[view].BODY} />;
  }

  return (
    <CenterFrameLayout title={text[view].TITLE} showLoader={text[view].SHOW_LOADER} modernLayout>
      <div>
        <div className="bangoMainText">{text[view].BODY}</div>
        {!!text[view].PRIMARY_BUTTON && !!text[view].PRIMARY_PATH && (
          <StaticLinkButton
            className="bangoPrimaryButton"
            path={text[view].PRIMARY_PATH}
            text={text[view].PRIMARY_BUTTON}
          />
        )}
        {!!text[view].SECONDARY_BUTTON && !!text[view].SECONDARY_PATH && (
          <a className="bangoSecondaryButton" href={text[view].SECONDARY_PATH}>
            {text[view].SECONDARY_BUTTON}
          </a>
        )}
      </div>
    </CenterFrameLayout>
  );
};
